import type { MutationLocus } from '../api/genetics';
import {
  InheritanceMode,
  LocusChromosome,
  parentPolygenicScore,
  polygenicIntensityLabel,
  type MutationLocusDef,
} from '@birdman/core';
import { Gender } from './enums';

export type LocusGenotypeStatus = string;

export interface PendingGenotype {
  locusId: string;
  alleles: string[];
}

export interface GeneticsSummary {
  mutationName: string;
  phenotype: string;
  genotype: string;
}

function wildTypeSymbol(locus: MutationLocus): string {
  return locus.alleles.find((a) => a.isWildType)?.symbol ?? locus.alleles[0]?.symbol ?? '';
}

function mutantAlleles(locus: MutationLocus) {
  return locus.alleles.filter((a) => !a.isWildType);
}

function primaryMutantSymbol(locus: MutationLocus): string {
  return mutantAlleles(locus)[0]?.symbol ?? locus.alleles[1]?.symbol ?? '';
}

function isSexLinked(locus: MutationLocus): boolean {
  return locus.chromosome === 'Z';
}

function isFemale(gender: Gender): boolean {
  return gender === Gender.FEMALE;
}

function toLocusDef(locus: MutationLocus): MutationLocusDef {
  return {
    symbol: locus.locusSymbol,
    name: locus.locusName,
    chromosome: locus.chromosome as LocusChromosome,
    inheritanceMode: locus.inheritanceMode as InheritanceMode,
    alleles: locus.alleles.map((a) => ({
      symbol: a.symbol,
      name: a.name,
      isWildType: a.isWildType,
      dominanceRank: a.dominanceRank,
      phenotypeDescription: a.phenotypeDescription,
    })),
  };
}

function sortedPolygenicMutants(locus: MutationLocus) {
  return [...mutantAlleles(locus)].sort(
    (a, b) => (a.dominanceRank ?? 1) - (b.dominanceRank ?? 1),
  );
}

export function getLocusStatusOptions(
  locus: MutationLocus,
  gender: Gender,
): Array<{ value: LocusGenotypeStatus; label: string }> {
  const wt = wildTypeSymbol(locus);
  const mutants = mutantAlleles(locus);
  const mutant = primaryMutantSymbol(locus);
  if (!wt || mutants.length === 0) {
    return [{ value: 'wild', label: 'Wild type' }];
  }

  if (locus.inheritanceMode === 'POLYGENIC') {
    const polyMutants = sortedPolygenicMutants(locus);
    const options: Array<{ value: LocusGenotypeStatus; label: string }> = [
      { value: 'wild', label: 'Wild type' },
    ];
    for (const m of polyMutants) {
      if (isSexLinked(locus) && isFemale(gender)) {
        options.push({
          value: `poly_hom_${m.symbol}`,
          label: `${m.name} (${m.symbol}/-)`,
        });
        continue;
      }
      options.push({
        value: `poly_dose_${m.symbol}`,
        label: `Low ${m.name} (${wt}/${m.symbol})`,
      });
      options.push({
        value: `poly_hom_${m.symbol}`,
        label: `High ${m.name} (${m.symbol}/${m.symbol})`,
      });
    }
    if (!isSexLinked(locus) && polyMutants.length > 1) {
      for (let i = 0; i < polyMutants.length; i++) {
        for (let j = i + 1; j < polyMutants.length; j++) {
          const a = polyMutants[i];
          const b = polyMutants[j];
          options.push({
            value: `poly_het_${a.symbol}_${b.symbol}`,
            label: `Mixed ${a.name}/${b.name} (${a.symbol}/${b.symbol})`,
          });
        }
      }
    }
    return options;
  }

  if (isSexLinked(locus) && mutants.length > 1) {
    if (isFemale(gender)) {
      return [
        { value: 'wild', label: `Wild type (${wt})` },
        ...mutants.map((m) => ({ value: `hemi_${m.symbol}`, label: `${m.name} (${m.symbol}/-)` })),
      ];
    }

    const options: Array<{ value: LocusGenotypeStatus; label: string }> = [
      { value: 'wild', label: `Wild type (${wt}/${wt})` },
    ];
    for (const m of mutants) {
      options.push({ value: `split_${m.symbol}`, label: `Split ${m.name} (${wt}/${m.symbol})` });
      options.push({ value: `visual_${m.symbol}`, label: `${m.name} (${m.symbol}/${m.symbol})` });
    }
    for (let i = 0; i < mutants.length; i++) {
      for (let j = i + 1; j < mutants.length; j++) {
        const a = mutants[i];
        const b = mutants[j];
        options.push({
          value: `het_${a.symbol}_${b.symbol}`,
          label: `${a.name}/${b.name} (${a.symbol}/${b.symbol})`,
        });
      }
    }
    return options;
  }

  if (isSexLinked(locus) && isFemale(gender)) {
    return [
      { value: 'wild', label: `Wild type (${wt})` },
      { value: 'hemizygous', label: `Visual (${mutant}/-)` },
    ];
  }

  if (locus.inheritanceMode === 'CODOMINANT' && mutants.length > 1) {
    const options: Array<{ value: LocusGenotypeStatus; label: string }> = [
      { value: 'wild', label: `Wild type (${wt}/${wt})` },
    ];
    for (const m of mutants) {
      options.push({ value: `split_${m.symbol}`, label: `Split ${m.name} (${wt}/${m.symbol})` });
      options.push({ value: `visual_${m.symbol}`, label: `${m.name} (${m.symbol}/${m.symbol})` });
    }
    for (let i = 0; i < mutants.length; i++) {
      for (let j = i + 1; j < mutants.length; j++) {
        const a = mutants[i];
        const b = mutants[j];
        options.push({
          value: `het_${a.symbol}_${b.symbol}`,
          label: `${a.name}/${b.name} (${a.symbol}/${b.symbol})`,
        });
      }
    }
    return options;
  }

  if (locus.inheritanceMode === 'AUTOSOMAL_INCOMPLETE_DOMINANT') {
    return [
      { value: 'wild', label: `Wild type (${wt}/${wt})` },
      { value: 'single_factor', label: `Single factor (${wt}/${mutant})` },
      { value: 'double_factor', label: `Double factor (${mutant}/${mutant})` },
    ];
  }

  if (locus.inheritanceMode === 'AUTOSOMAL_DOMINANT' || locus.inheritanceMode === 'SEX_LINKED_DOMINANT') {
    return [
      { value: 'wild', label: `Wild type (${wt}/${wt})` },
      { value: 'visual', label: `Visual (${wt}/${mutant})` },
    ];
  }

  return [
    { value: 'wild', label: `Wild type (${wt}/${wt})` },
    { value: 'split', label: `Split (${wt}/${mutant})` },
    { value: 'visual', label: `Visual (${mutant}/${mutant})` },
  ];
}

export function statusToAlleles(
  locus: MutationLocus,
  status: LocusGenotypeStatus,
  gender: Gender,
): string[] {
  const wt = wildTypeSymbol(locus);
  const mutant = primaryMutantSymbol(locus);

  if (status === 'wild') {
    return isSexLinked(locus) && isFemale(gender) ? [wt] : [wt, wt];
  }

  if (status.startsWith('poly_dose_')) {
    return [wt, status.slice(10)];
  }
  if (status.startsWith('poly_hom_')) {
    const symbol = status.slice(9);
    return isSexLinked(locus) && isFemale(gender) ? [symbol] : [symbol, symbol];
  }
  if (status.startsWith('poly_het_')) {
    const parts = status.slice(9).split('_');
    return parts.length === 2 ? parts : [wt, wt];
  }

  if (status.startsWith('hemi_')) {
    return [status.slice(5)];
  }
  if (status.startsWith('split_')) {
    return [wt, status.slice(6)];
  }
  if (status.startsWith('visual_')) {
    const symbol = status.slice(7);
    return [symbol, symbol];
  }
  if (status.startsWith('het_')) {
    const parts = status.slice(4).split('_');
    return parts.length === 2 ? parts : [wt, wt];
  }

  if (isSexLinked(locus) && isFemale(gender)) {
    return [mutant];
  }

  if (status === 'split' || status === 'single_factor') {
    return [wt, mutant];
  }

  return [mutant, mutant];
}

export function allelesToStatus(
  locus: MutationLocus,
  alleles: string[],
  gender: Gender,
): LocusGenotypeStatus {
  const wt = wildTypeSymbol(locus);
  const mutant = primaryMutantSymbol(locus);
  const mutants = mutantAlleles(locus);
  const sorted = [...alleles].sort().join('|');

  if (locus.inheritanceMode === 'POLYGENIC') {
    const polyMutants = sortedPolygenicMutants(locus);
    if (isSexLinked(locus) && isFemale(gender)) {
      const allele = alleles[0];
      if (!allele || allele === wt) return 'wild';
      return `poly_hom_${allele}`;
    }
    if (sorted === [wt, wt].sort().join('|')) return 'wild';
    for (const m of polyMutants) {
      if (sorted === [wt, m.symbol].sort().join('|')) return `poly_dose_${m.symbol}`;
      if (sorted === [m.symbol, m.symbol].sort().join('|')) return `poly_hom_${m.symbol}`;
    }
    for (let i = 0; i < polyMutants.length; i++) {
      for (let j = i + 1; j < polyMutants.length; j++) {
        if (sorted === [polyMutants[i].symbol, polyMutants[j].symbol].sort().join('|')) {
          return `poly_het_${polyMutants[i].symbol}_${polyMutants[j].symbol}`;
        }
      }
    }
    return 'wild';
  }

  if (
    (isSexLinked(locus) && mutants.length > 1) ||
    (locus.inheritanceMode === 'CODOMINANT' && mutants.length > 1)
  ) {
    if (isSexLinked(locus) && isFemale(gender)) {
      const allele = alleles[0];
      if (!allele || allele === wt) return 'wild';
      return `hemi_${allele}`;
    }

    if (sorted === [wt, wt].sort().join('|')) return 'wild';
    for (const m of mutants) {
      if (sorted === [wt, m.symbol].sort().join('|')) return `split_${m.symbol}`;
      if (sorted === [m.symbol, m.symbol].sort().join('|')) return `visual_${m.symbol}`;
    }
    for (let i = 0; i < mutants.length; i++) {
      for (let j = i + 1; j < mutants.length; j++) {
        if (sorted === [mutants[i].symbol, mutants[j].symbol].sort().join('|')) {
          return `het_${mutants[i].symbol}_${mutants[j].symbol}`;
        }
      }
    }
    return 'wild';
  }

  if (isSexLinked(locus) && isFemale(gender)) {
    return alleles[0] === mutant ? 'hemizygous' : 'wild';
  }

  if (sorted === [wt, wt].sort().join('|')) return 'wild';
  if (sorted === [wt, mutant].sort().join('|')) {
    return locus.inheritanceMode === 'AUTOSOMAL_INCOMPLETE_DOMINANT' ? 'single_factor' : 'split';
  }
  if (sorted === [mutant, mutant].sort().join('|')) {
    return locus.inheritanceMode === 'AUTOSOMAL_INCOMPLETE_DOMINANT' ? 'double_factor' : 'visual';
  }

  return 'wild';
}

function statusLabelForSummary(
  locus: MutationLocus,
  status: LocusGenotypeStatus,
  gender: Gender,
): {
  visual?: string;
  split?: string;
} {
  if (status === 'wild') return {};

  if (status.startsWith('poly_')) {
    const locusDef = toLocusDef(locus);
    const sex = isFemale(gender) ? 'FEMALE' : 'MALE';
    const alleles = statusToAlleles(locus, status, gender);
    const score = parentPolygenicScore(locusDef, alleles, sex);
    return { visual: polygenicIntensityLabel(locusDef, score) };
  }

  if (status.startsWith('hemi_') || status.startsWith('visual_')) {
    const symbol = status.split('_').pop()!;
    const allele = locus.alleles.find((a) => a.symbol === symbol);
    return { visual: allele?.name ?? locus.locusName };
  }

  if (status.startsWith('het_')) {
    const symbols = status.slice(4).split('_');
    const names = symbols
      .map((symbol) => locus.alleles.find((a) => a.symbol === symbol)?.name ?? symbol)
      .join('/');
    return { visual: names };
  }

  if (status.startsWith('split_')) {
    const symbol = status.slice(6);
    const allele = locus.alleles.find((a) => a.symbol === symbol);
    return { split: `${allele?.name ?? locus.locusName} split` };
  }

  if (status === 'visual' || status === 'double_factor' || status === 'hemizygous') {
    const mutant = primaryMutantSymbol(locus);
    const allele = locus.alleles.find((a) => a.symbol === mutant);
    return { visual: allele?.name ?? locus.locusName };
  }

  if (status === 'split' || status === 'single_factor') {
    return { split: `${locus.locusName} split` };
  }

  return {};
}

export function buildGeneticsSummary(
  loci: MutationLocus[],
  genotypes: PendingGenotype[],
  gender: Gender,
): GeneticsSummary {
  const visualLabels: string[] = [];
  const splitLabels: string[] = [];
  const genotypeLabels: string[] = [];

  for (const entry of genotypes) {
    const locus = loci.find((l) => l.id === entry.locusId);
    if (!locus) continue;

    const status = allelesToStatus(locus, entry.alleles, gender);
    if (status === 'wild') continue;

    const alleleLabel =
      isSexLinked(locus) && isFemale(gender)
        ? `${entry.alleles[0]}/-`
        : entry.alleles.join('/');

    genotypeLabels.push(`${locus.locusSymbol}:${alleleLabel}`);

    const labels = statusLabelForSummary(locus, status, gender);
    if (labels.visual) visualLabels.push(labels.visual);
    if (labels.split) splitLabels.push(labels.split);
  }

  return {
    mutationName: [...visualLabels, ...splitLabels].join(', '),
    phenotype: visualLabels.length ? visualLabels.join(', ') : 'Wild type',
    genotype: genotypeLabels.join('; '),
  };
}
